import { useRef, useEffect, useMemo } from 'react';

/**
 * KNKT galaxy logo — canvas-rendered, ported verbatim from the design-system
 * showcase (designapp.knkt.io/knkt-ai-chat). Procedurally generates stars
 * orbiting the "KNKT" letterform with a heartbeat-envelope pulse on the
 * central sun.
 *
 * Source: KNKT-design-app/showcase/src/components/AIChat/KNKTLogo.jsx
 */

const PI2 = Math.PI * 2;
const REF = 160;
const CORE_RADIUS = 32;

interface RGB { r: number; g: number; b: number }

function hexToRgb(hex: string | null | undefined): RGB {
  if (!hex || hex === 'transparent') return { r: 255, g: 255, b: 255 };
  let h = String(hex).replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  return {
    r: parseInt(h.substr(0, 2), 16),
    g: parseInt(h.substr(2, 2), 16),
    b: parseInt(h.substr(4, 2), 16),
  };
}

interface Star {
  size: number;
  alpha: number;
  orbitR: number;
  orbitA: number;
  speed: number;
  band: boolean;
}

function makeGalaxy(count: number, seed: number): Star[] {
  const stars: Star[] = [];
  let rng = seed >>> 0;
  const rand = () => {
    rng = (rng * 1664525 + 1013904223) >>> 0;
    return rng / 0xffffffff;
  };
  let safety = 0;
  while (stars.length < count && safety < count * 8) {
    safety++;
    const band = rand() < 0.62;
    let dist: number;
    let ax: number;
    if (band) {
      const ba = rand() * PI2;
      const br = 34 + rand() * 56;
      const sp = (rand() - 0.5) * 22;
      const tilt = 0.52;
      const bx = Math.cos(ba) * br + Math.cos(ba + PI2 / 4) * sp * tilt;
      const by = Math.sin(ba) * br + Math.sin(ba + PI2 / 4) * sp;
      dist = Math.sqrt(bx * bx + by * by);
      ax = Math.atan2(by, bx);
    } else {
      ax = rand() * PI2;
      dist = 30 + rand() * 68;
    }
    if (dist < CORE_RADIUS) continue;
    if (band) {
      stars.push({
        size: 0.4 + rand() * 1.4,
        alpha: 0.25 + rand() * 0.65,
        orbitR: dist, orbitA: ax,
        speed: (0.0008 + rand() * 0.0006) * (rand() < 0.5 ? 1 : -1),
        band: true,
      });
    } else {
      stars.push({
        size: 0.3 + rand() * 0.9,
        alpha: 0.12 + rand() * 0.28,
        orbitR: dist, orbitA: ax,
        speed: (0.0003 + rand() * 0.0004) * (rand() < 0.5 ? 1 : -1),
        band: false,
      });
    }
  }
  return stars;
}

function roundRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
}

interface KNKTLogoProps {
  size?: number;
  starCount?: number;
  bg?: string;
  sunColor?: string;
  starColor1?: string;
  starColor2?: string | null;
  spinSpeed?: number;
  direction?: 1 | -1;
  starSizeScale?: number;
  shape?: 'square' | 'circle' | 'none';
  pulseMode?: 'sine' | 'heartbeat';
  bpm?: number;
  beatStars?: boolean;
  isLoading?: boolean;
  borderRadius?: number;
  className?: string;
  ariaLabel?: string;
  onClick?: (e: React.MouseEvent) => void;
}

export function KNKTLogo({
  size = 80,
  starCount = 320,
  bg = '#04040e',
  sunColor = '#ffffff',
  starColor1 = '#ffffff',
  starColor2 = null,
  spinSpeed = 0.004,
  direction = 1,
  starSizeScale = 1,
  shape = 'circle',
  pulseMode = 'heartbeat',
  bpm = 120,
  beatStars = false,
  isLoading = false,
  onClick,
  borderRadius = 24,
  className = '',
  ariaLabel = 'KNKT',
}: KNKTLogoProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const burstRef = useRef({ active: false, t: 0 });
  const isLoadingRef = useRef(isLoading);
  const spinSpeedRef = useRef(spinSpeed);
  const directionRef = useRef(direction);
  const starSizeScaleRef = useRef(starSizeScale);
  const pulseModeRef = useRef(pulseMode);
  const bpmRef = useRef(bpm);
  const beatStarsRef = useRef(beatStars);

  useEffect(() => { isLoadingRef.current = isLoading; }, [isLoading]);
  useEffect(() => { spinSpeedRef.current = spinSpeed; }, [spinSpeed]);
  useEffect(() => { directionRef.current = direction; }, [direction]);
  useEffect(() => { starSizeScaleRef.current = starSizeScale; }, [starSizeScale]);
  useEffect(() => { pulseModeRef.current = pulseMode; }, [pulseMode]);
  useEffect(() => { bpmRef.current = bpm; }, [bpm]);
  useEffect(() => { beatStarsRef.current = beatStars; }, [beatStars]);

  const stars = useMemo(() => makeGalaxy(starCount, 42), [starCount]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;

    canvas.width = Math.round(size * dpr);
    canvas.height = Math.round(size * dpr);
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const sunRgb = hexToRgb(sunColor);
    const c1Rgb = hexToRgb(starColor1);
    const c2Rgb = starColor2 ? hexToRgb(starColor2) : null;
    const isTransparent = bg === 'transparent' || shape === 'none';
    const sc = size / REF;

    const applyShapePath = () => {
      if (shape === 'circle') {
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, PI2);
        ctx.closePath();
      } else if (shape === 'square') {
        roundRectPath(ctx, 0, 0, size, size, borderRadius);
      }
    };

    let t = 0;
    let running = true;
    const startedAt = performance.now();

    const heartbeatEnvelope = (phase: number) => {
      const s1 = Math.exp(-Math.pow((phase - 0.06) / 0.035, 2));
      const s2 = 0.55 * Math.exp(-Math.pow((phase - 0.18) / 0.045, 2));
      return Math.min(1, s1 + s2);
    };

    const frame = () => {
      if (!running) return;
      const dir = directionRef.current >= 0 ? 1 : -1;
      const rawSpin = isLoadingRef.current ? spinSpeedRef.current * 3 : spinSpeedRef.current;
      const effectiveSpin = rawSpin * dir;

      const elapsed = (performance.now() - startedAt) / 1000;
      const useBeat = pulseModeRef.current === 'heartbeat' && bpmRef.current > 0;
      const bpmNow = Math.max(20, Math.min(220, bpmRef.current || 120));
      const beatPhase = useBeat ? (elapsed * bpmNow / 60) % 1 : 0;
      const beatEnv = useBeat ? heartbeatEnvelope(beatPhase) : 0;
      const sizeMul = starSizeScaleRef.current;

      ctx.clearRect(0, 0, size, size);

      if (!isTransparent) {
        ctx.fillStyle = bg;
        applyShapePath();
        ctx.fill();
      }

      ctx.save();
      if (shape !== 'none') {
        applyShapePath();
        ctx.clip();
      }

      const cx = size / 2;
      const cy = size / 2;

      const starBeatPush = (useBeat && beatStarsRef.current) ? beatEnv * 0.18 : 0;
      const starBeatBoost = (useBeat && beatStarsRef.current) ? 1 + beatEnv * 0.45 : 1;

      for (let i = 0; i < stars.length; i++) {
        const s = stars[i];
        const a = s.orbitA + t * s.speed * dir + t * effectiveSpin * 0.4;
        const radius = s.orbitR * sc * (1 + starBeatPush);
        const px = cx + Math.cos(a) * radius;
        const py = cy + Math.sin(a) * radius;
        const tw = 0.7 + 0.3 * Math.sin(t * 0.08 + i * 1.3);
        const col = c2Rgb && i % 3 === 0 ? c2Rgb : c1Rgb;
        const r = Math.max(0.4, s.size * sc * sizeMul);
        ctx.beginPath();
        ctx.arc(px, py, r, 0, PI2);
        ctx.fillStyle = `rgba(${col.r},${col.g},${col.b},${Math.min(1, s.alpha * tw * starBeatBoost)})`;
        ctx.fill();
      }

      if (burstRef.current.active) {
        burstRef.current.t++;
        const p = burstRef.current.t / 45;
        const maxR = (size / 2) * 0.92;
        ctx.beginPath();
        ctx.arc(cx, cy, p * maxR, 0, PI2);
        ctx.strokeStyle = `rgba(${sunRgb.r},${sunRgb.g},${sunRgb.b},${Math.max(0, 0.75 - p * 0.9)})`;
        ctx.lineWidth = Math.max(1, 1.8 * sc);
        ctx.stroke();
        for (let k = 0; k < 6; k++) {
          const ang = k * PI2 / 6;
          const rx = cx + Math.cos(ang) * p * maxR * 0.92;
          const ry = cy + Math.sin(ang) * p * maxR * 0.92;
          const sr = Math.max(0.8, 4 * sc * (1 - p));
          ctx.beginPath();
          ctx.arc(rx, ry, sr, 0, PI2);
          ctx.fillStyle = `rgba(${sunRgb.r},${sunRgb.g},${sunRgb.b},${Math.max(0, 1 - p * 1.2)})`;
          ctx.fill();
        }
        if (burstRef.current.t > 45) {
          burstRef.current.active = false;
          burstRef.current.t = 0;
        }
      }

      const pulse = useBeat
        ? 0.85 + beatEnv * 0.32
        : 0.88 + Math.sin(t * 0.035) * 0.12;

      const outerR = 34 * sc * pulse;
      const outerGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, outerR);
      outerGrad.addColorStop(0, `rgba(${sunRgb.r},${sunRgb.g},${sunRgb.b},0.22)`);
      outerGrad.addColorStop(0.5, `rgba(${sunRgb.r},${sunRgb.g},${sunRgb.b},0.08)`);
      outerGrad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.beginPath();
      ctx.arc(cx, cy, outerR, 0, PI2);
      ctx.fillStyle = outerGrad;
      ctx.fill();

      const innerR = 18 * sc * pulse;
      const innerGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, innerR);
      innerGrad.addColorStop(0, `rgba(${sunRgb.r},${sunRgb.g},${sunRgb.b},0.48)`);
      innerGrad.addColorStop(0.6, `rgba(${sunRgb.r},${sunRgb.g},${sunRgb.b},0.16)`);
      innerGrad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.beginPath();
      ctx.arc(cx, cy, innerR, 0, PI2);
      ctx.fillStyle = innerGrad;
      ctx.fill();

      const fontPx = Math.max(6, 11 * sc);
      ctx.font = `400 ${fontPx}px "DM Sans", Inter, "Helvetica Neue", Arial, sans-serif`;
      ctx.fillStyle = `rgba(${sunRgb.r},${sunRgb.g},${sunRgb.b},${0.72 + 0.28 * pulse})`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      const letters = ['K', 'N', 'K', 'T'];
      const spacing = Math.max(1, 5 * sc);
      const widths = letters.map(l => ctx.measureText(l).width);
      const total = widths.reduce((a, b) => a + b, 0) + spacing * (letters.length - 1);
      let xPos = cx - total / 2;
      for (let i = 0; i < letters.length; i++) {
        ctx.fillText(letters[i], xPos + widths[i] / 2, cy);
        xPos += widths[i] + spacing;
      }

      ctx.restore();
      t++;
      rafRef.current = requestAnimationFrame(frame);
    };

    rafRef.current = requestAnimationFrame(frame);

    return () => {
      running = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [size, stars, bg, sunColor, starColor1, starColor2, borderRadius, shape]);

  const handleClick = (e: React.MouseEvent) => {
    burstRef.current.active = true;
    burstRef.current.t = 0;
    if (onClick) onClick(e);
  };

  return (
    <canvas
      ref={canvasRef}
      onClick={handleClick}
      className={className}
      role="img"
      aria-label={ariaLabel}
      style={{ display: 'block', cursor: onClick ? 'pointer' : 'default' }}
    />
  );
}

export default KNKTLogo;
